import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ChildOnboardingStepService } from '../../application/child-onboarding-step/child-onboarding-step.service';
import { ChildOnboardingStatus } from '../../entities/child-onboarding/child-onboarding-status.enum';
import { ChildOnboarding } from '../../entities/child-onboarding/child-onboarding.model';

@Pipe({
  name: 'status',
  standalone: true,
})
export class StatusPipe implements PipeTransform {
  readonly #stepService = inject(ChildOnboardingStepService);
  readonly #translocoService = inject(TranslocoService);

  readonly #translate = (key: string): string => this.#translocoService.translate(key);

  public transform(data: ChildOnboarding, isLg2: boolean): string {
    if (!data) {
      return '&mdash;';
    }

    const prefix = 'child-onboarding.onboardings-list.status.';

    const isConfirmed = data.processStatus === ChildOnboardingStatus.CONFIRMED;
    if (isConfirmed) {
      return this.#translate(`${prefix}success`);
    }

    if (isLg2) {
      return this.#getLegalGuardian2Status(data, prefix);
    }

    return this.#getPrimaryGuardianStatus(data, prefix);
  }

  #getLegalGuardian2Status(data: ChildOnboarding, prefix: string): string {
    const isAwaitingConfirmation = data.processStatus === ChildOnboardingStatus.AWAITING_LG2CONFIRMATION;
    const isEmailVerified = data.cEmailVerified;

    if (isAwaitingConfirmation) {
      return this.#translate(`${prefix}lg2.default`);
    }

    const statusKey = isEmailVerified ? 'emailConfirmed' : 'emailConfirm';
    return this.#translate(`${prefix}lg2.${statusKey}`);
  }

  #getPrimaryGuardianStatus(data: ChildOnboarding, prefix: string): string {
    // Handle regular step status
    const step = this.#stepService.getNextStep(data);
    if (step !== null) {
      const key = prefix + step;
      const translation = this.#translate(key);

      // Fallback to success if translation key doesn't exist
      return translation === key ? this.#translate(`${prefix}success`) : translation;
    }

    // No next step but not yet confirmed
    return this.#getFinalStepStatus(data, prefix);
  }

  #getFinalStepStatus(data: ChildOnboarding, prefix: string): string {
    const isAwaitingLg2Confirmation = data.processStatus === ChildOnboardingStatus.AWAITING_LG2CONFIRMATION;
    const statusKey = isAwaitingLg2Confirmation ? 'contractDone' : 'conclusionOfContract';

    return this.#translate(`${prefix}${statusKey}`);
  }
}
